import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { CancelSubmitButtons } from "~/common/components";
import Modal, { ModalProps } from "~/common/components/Modal";
import { QUESTION_TYPES } from "~/common/utils";
import {
  EMPTY_CHOICE,
  QUESTION_CHOICE_SELECTION_METHOD,
  QUESTION_TYPE,
} from "~/providers/constants/supplementalQuestions";
import {
  useCreateSupplementalQuestion,
  useGetCurrentOrganizationId,
} from "~/providers/hooks";
import { addQuestionFormSchema } from "~/providers/schemas/supplementalQuestions";
import { AddQuestionForm } from "~/providers/types/supplementalQuestions";
import { useSupplementalQuestionsEditorContext } from "./SupplementalQuestionsEditor";
import { SupplementalQuestionUpsertForm } from "./SupplementalQuestionUpsertForm";

export const AddSupplementalQuestionModal = ({
  onClose,
  ...props
}: ModalProps) => {
  const organizationId = useGetCurrentOrganizationId();
  const { type: consultationType } = useSupplementalQuestionsEditorContext();
  const createSupplementalQuestionMutation = useCreateSupplementalQuestion();

  const addQuestionForm = useForm<AddQuestionForm>({
    resolver: zodResolver(addQuestionFormSchema),
    mode: "onChange",
    defaultValues: {
      type: undefined,
      selectionMethod: QUESTION_CHOICE_SELECTION_METHOD.SINGLE,
      text: "",
      choices: Array(2).fill(EMPTY_CHOICE),
    },
  });

  const selectedType = addQuestionForm.watch("type");

  const onSubmit = (data: AddQuestionForm) => {
    if (!organizationId) return;

    const type =
      data.type === QUESTION_TYPE.MULTIPLE_CHOICE
        ? data.selectionMethod === QUESTION_CHOICE_SELECTION_METHOD.MULTIPLE
          ? QUESTION_TYPES.supplemental_multi_select
          : QUESTION_TYPES.supplemental_single_select
        : data.type === QUESTION_TYPE.YES_NO
          ? QUESTION_TYPES.supplemental_single_select
          : QUESTION_TYPES.supplemental_free_text;

    const choices =
      data.type === QUESTION_TYPE.MULTIPLE_CHOICE
        ? (data.choices.map((choice) => choice.label) ?? [])
        : data.type === QUESTION_TYPE.YES_NO
          ? ["yes", "no"]
          : [];

    createSupplementalQuestionMutation.mutate(
      {
        consultationType,
        organizationId,
        text: data.text,
        type,
        choices,
      },
      {
        onSuccess: () => {
          handleResetAndClose();
        },
      },
    );
  };

  const handleResetAndClose = () => {
    onClose();
    addQuestionForm.reset({ type: undefined });
  };

  return (
    <Modal
      title={`Add question to ${consultationType}`}
      className={twMerge(
        "max-h-[80vh] overflow-scroll xl:w-1/2",
        !selectedType && "overflow-visible",
      )}
      onClose={handleResetAndClose}
      {...props}
    >
      <FormProvider {...addQuestionForm}>
        <form
          className="flex flex-col gap-6"
          onSubmit={addQuestionForm.handleSubmit(onSubmit)}
        >
          <SupplementalQuestionUpsertForm />

          <CancelSubmitButtons
            submitButtonLabel="Add question"
            handleCancel={handleResetAndClose}
            isSubmitting={createSupplementalQuestionMutation.isPending}
            containerClassName="w-fit ml-auto"
            disableSubmit={!addQuestionForm.formState.isValid}
          />
        </form>
      </FormProvider>
    </Modal>
  );
};
